import React,{ useState, useEffect,useRef } from "react";
import { fetchByJSON, useCachedData } from '../../util/util'
import { useSelector } from 'react-redux'
import { thebeConfigR } from "../../util/thebeConfigs";
import { usePageKernel, attachCells } from "../../util/thebeSession";
import { Modal, Label } from '../../util/ui'
import { DropDown, Button } from '../../util/ui_components'
import './index.css'
import sanitizeJsonString from "../common/sanitize";

// const reconnectErrorFunction = () => {
//   alert('Kernel is taking too much time. Please reduce the data size!')
// }

const reconnectErrorFunction = () => {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 48px;
    height: 48px;
    border: 5px solid rgba(63, 91, 203, 0.84);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `;

  document.head.appendChild(style);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.remove();
    style.remove();
  }, 1000);
};


const getInitialCode = data => `
library(jsonlite)
data_json <- '${data}'
# Convert JSON to a list
df_list <- fromJSON(data_json)
columns_list <- lapply(df_list, unlist)

# Combine the list into a proper data frame
df <- as.data.frame(do.call(cbind, df_list),check.names = FALSE)
df <- as.data.frame(columns_list, stringsAsFactors = FALSE,check.names = FALSE)
df <- type.convert(df, as.is = TRUE)
`


const InitialCodeR = (data) => `

library(jsonlite)
data_json <- '${data}'

# Convert JSON to a list. pandas' df.to_json() omits the row-index key entirely for any
# NaN cell (rather than including it as null), so columns with missing values end up with
# fewer entries than columns with none -- reindex every column against the union of all
# row indices first, so missing cells become proper NA in the right position, before
# combining into a data frame.
df_list <- fromJSON(data_json)
all_indices <- unique(unlist(lapply(df_list, names)))
all_indices <- all_indices[order(as.numeric(all_indices))]
# fromJSON parses some columns as plain named vectors and others (e.g. ones with gaps
# from missing values) as lists -- single-bracket indexing behaves inconsistently between
# the two (NA vs a NULL list element), so check membership explicitly and always pull
# scalars out with [[, producing a flat, correctly-NA'd vector either way.
columns_list <- lapply(df_list, function(col) {
    col_names <- names(col)
    sapply(all_indices, function(idx) {
        if (idx %in% col_names) {
            val <- col[[idx]]
            if (is.null(val)) NA else val
        } else {
            NA
        }
    })
})
names(columns_list) <- names(df_list)

# Combine the list into a proper data frame
df_r <- as.data.frame(columns_list, stringsAsFactors = FALSE, check.names = FALSE)
df_r <- type.convert(df_r, as.is = TRUE)
`

const defaultRCode = `
# Inspect the dataframe 'df'
class(df_r)

# Inspect the dataframe 'df'
names(df_r)
str(df_r)
dim(df_r)
head(df_r)
`;



const supportCodeR = `
library(readr) # For reading CSV files
library(dplyr) # For data manipulation
library(ggplot2) # For data visualization

# Manually load other libraries if necessary

# Replace '<filename.csv>' with the dataset you need
df <- read_csv('<filename.csv>')

`

//https://stackoverflow.com/questions/3903488/javascript-backslash-in-variables-is-causing-an-error
const stringEscape = (s)=>{
    return s ? s.replace(/\\/g,'\\\\').replace(/\n/g,'\\n').replace(/\t/g,'\\t').replace(/\v/g,'\\v').replace(/'/g,"\\'").replace(/"/g,'\\"').replace(/[\x00-\x1F\x80-\x9F]/g,hex) : s;
    function hex(c) { var v = '0'+c.charCodeAt(0).toString(16); return '\\x'+v.substr(v.length-2); }
}

const Page = ()=>{
    // The kernel connection and the dataset load independently of each other, so they get
    // a state each -- a single statusText meant whichever finished last overwrote the
    // other, and "No data" (set synchronously on mount, before redux has rehydrated the
    // dataset) would sit there for the whole binder launch instead of "Loading...".
    // busyText is the transient message for an operation the user just started, and wins
    // over both while it is set.
    let [kernelStatus, setKernelStatus] = useState('loading') // loading | ready | error | dead
    let [hasData, setHasData] = useState(false)
    let [busyText, setBusyText] = useState('')
    let [runningtext, setrunningtext] = useState('Run')
    let [dfJSON, setDfJSON] = useState('') //dataframe json
    let mutated = useRef(1)
    let dataset = useSelector(e=>e.dataset)
    let kernelRef = useRef(null)
    // one kernel per mounted page, shut down again on unmount
    const getPageKernel = usePageKernel(thebeConfigR)
    let [code, setCode] = useState([])
    let [downloadVarName, setDownloadVarName] = useState('')
    let [downloadFilename, setDownloadFilename] = useState('')
    let [showDatasetModal, setShowDatasetModal] = useState(false)

    // "no dataset" is the actionable one, so surface it even while the kernel is still
    // starting -- the binder launch can take minutes, and sitting on a bare "Loading..."
    // hides the fact that the user needs to go and load a dataset first.
    const statusText = busyText
        || (kernelStatus === 'dead' ? 'R kernel died - refresh the page to start a new one'
        : kernelStatus === 'error' ? 'Error connecting to R'
        : !hasData && kernelStatus === 'loading' ? 'No dataset loaded - load a dataset first (starting R kernel...)'
        : !hasData ? 'No dataset loaded - load a dataset first'
        : kernelStatus === 'loading' ? 'Starting R kernel...'
        : 'Ready')

    //load components on page loading
    useEffect(()=>{
        let isActive = true
        reconnectErrorFunction();
        getPageKernel().then((kernel) => {
            if (isActive) {
                kernelRef.current = kernel
                kernelRef.current._reconnect = reconnectErrorFunction;
                // Normally a no-op: starting the kernel also rendered this page's
                // <pre data-executable>. Kept as the safety net for the case where the
                // element only reaches the DOM after the kernel is already up.
                attachCells(kernel)
                // Move the output area to the right part if available.
                const outputArea = document.querySelector('.jp-OutputArea');
                const rightPart = document.querySelector('#rightPart');
                if (outputArea && rightPart) {
                    rightPart.appendChild(outputArea);
                }
                // Without this the status sits on "Ready" after the kernel dies (pod
                // culled, OOM, restart) and the only feedback is thebe's own
                // "Failed to execute. Error: Kernel is dead" inside the output area.
                if (kernel.statusChanged && typeof kernel.statusChanged.connect === 'function') {
                    kernel.statusChanged.connect((_sender, status) => {
                        if (isActive && status === 'dead') setKernelStatus('dead')
                    })
                }
                setKernelStatus('ready')
            }
        }).catch((err) => {
            if (isActive) {
                console.error('Error connecting to the R kernel:', err)
                setKernelStatus('error')
            }
        })

        return () => {
            isActive = false
            kernelRef.current = null
        }
    },[])

    useEffect(() => {
        reconnectErrorFunction();
        if (!dataset.filename) {
            setHasData(false)
            return
        }

        let isActive = true

        // This page's kernel is established once, in the mount effect above. This effect
        // used to also call thebelab.bootstrap() here, which requested a second kernel --
        // causing df_r to sometimes get created (by the Run button's boilerplate step) on
        // a different kernel than the one the visible cell's own run button actually
        // executes against. It only needs to (re-)fetch the current dataset's JSON
        // whenever it changes, not request another kernel.
        async function loadData() {
            let res = await fetchByJSON('current_data_json', {
                filename: dataset.filename
            })

            let g = await res.json()
            if (!isActive) return
            let json_string = sanitizeJsonString(g.data);
            setDfJSON(json_string)
            setHasData(true)
        }
        loadData()

        return () => { isActive = false }
    }, [dataset.filename])

    const onDownload = () => {
        const element = document.createElement('a')
        const file = new Blob([supportCodeR, ...code], {type: "text/plain"})
        element.href = URL.createObjectURL(file)
        element.download = "download_test.r" // or .txt
        document.body.appendChild(element)
        element.click()
    }

    // Downloads an R data frame/dataset (e.g. one loaded via a package, like `juul` from
    // ISwR) that only exists in the remote kernel's memory. write.csv() alone would just
    // write the file on the remote Binder machine, which the user can never reach -- so
    // instead we ask the kernel to print the CSV text to stdout and capture it over the
    // IOPub stream, then hand it to the browser as a real client-side file download.
    const onDownloadDataset = async () => {
        if (!kernelRef.current) {
            alert('The R kernel is not connected yet -- wait for status "Ready" and try again.')
            return
        }
        const varName = downloadVarName.trim()
        if (!varName) {
            alert('Enter the name of the R variable/dataset you want to download (e.g. juul).')
            return
        }

        try {
            setBusyText('Preparing dataset download ...')
            let csvText = ''
            let rError = null
            let future = kernelRef.current.requestExecute({
                code: `cat(paste(capture.output(write.csv(${varName}, row.names = FALSE)), collapse = "\\n"))`
            })
            future.onIOPub = (msg) => {
                if (msg.header.msg_type === 'stream' && msg.content.name === 'stdout') {
                    csvText += msg.content.text
                } else if (msg.header.msg_type === 'error') {
                    rError = `${msg.content.ename}: ${msg.content.evalue}`
                    console.error('R error while exporting dataset:', msg.content)
                }
            }
            await future.done

            if (rError) {
                prompt(`R error while exporting "${varName}" (select all + copy):`, rError)
                setBusyText('')
                return
            }

            if (!csvText) {
                alert(`No data captured for "${varName}" -- make sure that object exists and is a data frame in your current R session (run your code first).`)
                setBusyText('')
                return
            }

            const element = document.createElement('a')
            const file = new Blob([csvText], { type: 'text/csv' })
            element.href = URL.createObjectURL(file)
            element.download = (downloadFilename.trim() || `${varName}.csv`)
            document.body.appendChild(element)
            element.click()
            document.body.removeChild(element)
            setBusyText('')
            setShowDatasetModal(false)
        } catch (err) {
            console.error('Error during dataset download:', err)
            setBusyText('Error during dataset download')
        }
    }

    return (<div className='flex flex-col h-full justify-between gap-2'>
        <div className="flex w-full justify-center items-center m-0 flex-grow-0 h-20">
            <div className="status-text">{statusText}</div>
        </div>
        <div className="flex justify-between items-center margin-left">
        </div>
        <div className="flex w-full h-full justify-between flex-grow-1 gap-2">
            <div className="code w-1/2 border-2 m-2 box-content flex flex-col">
                <div className="flex justify-between items-center bg-gray-400">
                    <div className='run' onClick={async ()=>{
                        const kernel = kernelRef.current
                        if (!kernel) {
                            alert('The R kernel is not connected yet -- wait for status "Ready" and try again.')
                            return
                        }
                        try {
                            setBusyText('Running Initial Code ...');
                        mutated.current = 0
                        let res = await fetchByJSON('current_data_json', {
                            filename: dataset.filename
                        })
                        
                        let json = await res.json();
                        let json_string = sanitizeJsonString(json.data);
                        setDfJSON(json_string)
                        // console.log(json_string)
                        let executeCode = InitialCodeR(json_string);
                        // console.log(executeCode)
                        let setupError = null
                        let future = kernel.requestExecute({ code: executeCode });
                        future.onIOPub = (msg) => {
                            if (msg.header.msg_type === 'error') {
                                setupError = `${msg.content.ename}: ${msg.content.evalue}`
                                console.error('R error while setting up df_r:', msg.content)
                            }
                        }
                        let res2 = await future.done;
                        console.log(res2)
                        if (setupError) {
                            prompt('R error while preparing df_r (select all + copy):', setupError)
                            setBusyText('Error during run');
                            return
                        }
                        // setrunningtext('Running')
                        document.querySelector('.thebelab-run-button').click()

                        // catch user input code for download option
                        let appendCode = []
                        document.querySelector('#leftPart').querySelectorAll('span[role="presentation"]').forEach((node)=>{appendCode.push('\n' + node.textContent)})
                        setCode(appendCode)
                        setBusyText('');
                        } catch (err) {
                            console.error("Error during run:", err);
                            setBusyText('Error during run');
                        }
                        // setrunningtext('Run')
                        // setCode(document.querySelector('#leftPart').querySelector('span[role="presentation"]').textContent)
                        // console.log("test", document.querySelector('#leftPart').querySelectorAll('span'))
                    }}>Run</div>
                    <DropDown id="downloadOptions" text="Download" width="w-48" zIndex={100} items={[
                        { name: 'Download Code', onClick: onDownload },
                        { name: 'Download Dataset', onClick: () => setShowDatasetModal(true) }
                    ]} />
                </div>
                <pre data-executable="true" data-language="R" id="leftPart">{defaultRCode}</pre>
            </div>
            <div className="result w-1/2 border-2 m-2 box-content" id="rightPart">

            </div>
        </div>

        <Modal isOpen={showDatasetModal} setIsOpen={setShowDatasetModal} onClose={() => setShowDatasetModal(false)}>
            <div className="flex flex-col items-center justify-center py-10">
                <h1 className="text-3xl font-bold mb-6">Download Dataset</h1>
                <div className="w-full max-w-md bg-white p-6 rounded shadow-md">
                    <Label text="Code to save dataframe to a file :" />
                    <pre className="mb-4 bg-gray-100 p-4 rounded border border-gray-300 text-sm font-mono">
                        {`write.csv(${downloadVarName || '<dataset_name>'}, '${downloadFilename.trim() || (downloadVarName ? `${downloadVarName}.csv` : 'out.csv')}', row.names = FALSE)`}
                    </pre>
                    <div className="mb-4">
                        <Label text="Dataset variable name (e.g. juul) :" />
                        <input
                            type="text"
                            value={downloadVarName}
                            onChange={(e) => setDownloadVarName(e.target.value)}
                            placeholder="Enter the R object name from your session"
                            className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div className="mb-4">
                        <Label text="Filename (optional) :" />
                        <input
                            type="text"
                            value={downloadFilename}
                            onChange={(e) => setDownloadFilename(e.target.value)}
                            placeholder="Enter desired filename"
                            className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button
                            text="Download"
                            onClick={onDownloadDataset}
                            width="w-32"
                            customStyle={{ padding: "0.5rem 1rem" }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    </div>)
}

export default Page;
